import { ToastContext } from "context/ToastContext";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { holdEligibilityActivity } from "pages/CreateOrder/hooks/eligibilityActivity";
import { Button } from "primereact/button";
import { useContext, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import { useCreateEligibilityMutation } from "store/queries/eligibility";
import {
  IEligibilityMember,
  IEligibilityProvider,
  IProvider,
  IRecommendedPayerBasedOnEligibility,
} from "types/Provider/Provider";

import {
  buildOutOfNetworkOnlyBenefitsMessage,
  buildPayerSwitchMessage,
} from "../eligibilityMessages";
import { buildEligibilityProcedures } from "../eligibilityProcedures";
import {
  IOutOfNetworkExceptionEventDetail,
  OUT_OF_NETWORK_EXCEPTION_EVENT,
} from "../outOfNetworkExceptionEvent";
import {
  announcePracticeTypeRunCompleted,
  announcePracticeTypeRunSettled,
  IPracticeTypeRunEventDetail,
  PRACTICE_TYPE_RUN_EVENT,
} from "../practiceTypeRunEvent";
import { buildPracticeTypeSwitchMessage } from "../practiceTypeSwitchMessage";

interface IRunEligibilityOptions {
  /** CLE-2883 — staff obtained a gap / out-of-network exception from the payer. */
  outOfNetworkExceptionApproved?: boolean;
  /** CLE-2971 — run exactly the chosen Service Type; the API must not switch it. */
  keepPracticeType?: boolean;
}

export function EligibilityButton({
  accountId,
  mainProvider,
  errorMessageSetter,
  disabled,
  isSecondaryInsurance = false,
}: {
  accountId: string;
  mainProvider: IProvider;
  errorMessageSetter: React.Dispatch<React.SetStateAction<string | null>>;
  disabled: boolean;
  isSecondaryInsurance?: boolean;
}) {
  const { getValues, setValue, watch } = useFormContext();
  const toast = useContext(ToastContext);
  const [runningEligibility, setRunningEligibility] = useState(false);
  // CLE-2971 — true for the whole of a run (request AND storing its response). A Service Type run request
  // arriving meanwhile is HELD in `pendingRunRef` (latest wins) and run once this one settles, never dropped:
  // Lincoln staff click Run Eligibility and type the CPT codes while it is in flight.
  const inFlightRef = useRef(false);
  const pendingRunRef = useRef<IPracticeTypeRunEventDetail | null>(null);
  // CLE-2971 — the Service Type a "Keep" run kept IN THIS PAGE SESSION, or null. A later plain run sends
  // `keepPracticeType` while the dropdown still holds it. Never read from, or written to, the stored request:
  // that would carry the choice into copies, redos and reopened drafts and silently disable the switch there.
  const keptPracticeTypeRef = useRef<string | null>(null);
  const watchedPracticeType = watch(
    isSecondaryInsurance ? "practiceType2" : "practiceType"
  );
  useEffect(() => {
    if (
      keptPracticeTypeRef.current !== null &&
      watchedPracticeType !== keptPracticeTypeRef.current
    )
      keptPracticeTypeRef.current = null;
  }, [watchedPracticeType]);
  const { payersCodesInfo, payersNameInfo } = useSelector(
    (state: any) => state.orderSlice
  );
  const [createEligibility] = useCreateEligibilityMutation();

  const dateOfServiceStart = dayjs().format("MM/DD/YYYY");
  const dateOfServiceEnd = dayjs().format("MM/DD/YYYY");

  const mapValuesToEligibility = async ({
    outOfNetworkExceptionApproved = false,
    keepPracticeType = false,
  }: IRunEligibilityOptions = {}) => {
    errorMessageSetter("");
    const { name: providerName, npi } = mainProvider;
    const {
      firstName: memberFirstName,
      lastName: memberLastName,
      dateOfBirth: memberDOB,
      memberId,
      memberId2,
      payerName,
      payerName2,
      isSubscriberPatient,
      practiceType,
      practiceType2,
      serviceDetails,
    } = getValues();
    const practiceTypeCode = isSecondaryInsurance
      ? practiceType2
      : practiceType;

    // CLE-2971 — a "Keep" choice sticks for this page session: while the dropdown still holds the code a
    // Keep run kept, a plain re-run keeps it too. Changing the dropdown ends it (see `keptPracticeTypeRef`).
    const keep =
      keepPracticeType ||
      (keptPracticeTypeRef.current !== null &&
        keptPracticeTypeRef.current === practiceTypeCode);

    // CLE-2820 — the procedures already chosen, if any, for the AI second opinion's context only.
    const procedures = buildEligibilityProcedures(serviceDetails);

    const provider: IEligibilityProvider = {
      firstName: providerName,
      middleName: providerName,
      lastName: providerName,
      npi,
    };

    const member: IEligibilityMember = {
      firstName: memberFirstName,
      lastName: memberLastName,
      dob: memberDOB,
      memberID: isSecondaryInsurance ? memberId2 : memberId,
    };

    const payerCodeForPrimaryInsurance =
      payersCodesInfo.find(
        (payer: { name: string; code: string[] }) => payer.name === payerName
      )?.code[0] || "";
    const payerCodeForSecondaryInsurance =
      payersCodesInfo.find(
        (payer: { name: string; code: string[] }) => payer.name === payerName2
      )?.code[0] || "";

    const payerCode = isSecondaryInsurance
      ? payerCodeForSecondaryInsurance
      : payerCodeForPrimaryInsurance;

    const eligibility = {
      accountId,
      payerCode: payerCode || "",
      payerName: isSecondaryInsurance ? payerName2 : payerName,
      provider,
      member,
      isSubscriberPatient: Boolean(isSubscriberPatient),
      // CLE-2729 — the server cannot work this out for itself. Nothing in a pVerify response says
      // which side of the order it serves, and the order link that would say so is written after
      // the check returns. It is what keeps a secondary plan's $0 — which is $0 only because the
      // primary already paid — out of the primary side of the historical benefit index.
      isSecondaryInsurance,
      practiceTypeCode,
      dateOfServiceStart,
      dateOfServiceEnd,
      ...(procedures.length > 0 && { procedures }),
      // CLE-2883 — only the banner's "Exception is approved" button sends this. The normal Run Eligibility
      // button never does, which is how an approved exception is undone.
      ...(outOfNetworkExceptionApproved && {
        outOfNetworkExceptionApproved: true,
      }),
      // CLE-2971 — only "Keep …" (and a re-run of a kept check) sends this; see `keep` above.
      ...(keep && { keepPracticeType: true }),
    };
    setRunningEligibility(true);
    let eligibilityCall;
    try {
      eligibilityCall = await createEligibility({ body: eligibility });
    } finally {
      setRunningEligibility(false);
    }
    const data = (eligibilityCall as any)?.data?.data || null;

    return { request: eligibility, response: data };
  };

  const runEligibilityOnce = async (options: IRunEligibilityOptions = {}) => {
    const mapped = await mapValuesToEligibility(options);
    const eligibilityResponse = mapped.response;

    if (isEmpty(eligibilityResponse)) {
      errorMessageSetter(`No eligibility response found. 
      Please contact the payer via phone or payer's portal to obtain the patient's current benefit information`);
      return;
    }

    if (Object.prototype.hasOwnProperty.call(eligibilityResponse, "error")) {
      errorMessageSetter(
        `Eligibility Verification Error: ${eligibilityResponse.error}`
      );
      return;
    }

    if (eligibilityResponse.planStatus === "Inactive") {
      errorMessageSetter(
        "The patient coverage shows inactive, please contact the patient or the payer to obtain most current information"
      );
      return;
    }

    // CLE-2971 — the API ran the check under another Service Type because of the order's CPT codes.
    // The dropdown and the stored request follow the code that RAN: the payer form treats a request
    // whose code differs from the dropdown as stale, and draft / copy / redo restore the dropdown from
    // the stored request. The notice beside the dropdown reads the switch off the stored response.
    const switchedTo: string | undefined =
      eligibilityResponse.practiceTypeSwitch?.to || undefined;
    // `keepPracticeType` is never stored: a stored request outlives this page (copy, redo, reopened draft).
    const { keepPracticeType: sentKeep, ...requestToStore } = mapped.request;
    const eligibilityData = {
      ...mapped,
      request: switchedTo
        ? { ...requestToStore, practiceTypeCode: switchedTo }
        : requestToStore,
    };
    if (switchedTo) {
      setValue(
        isSecondaryInsurance ? "practiceType2" : "practiceType",
        switchedTo
      );
      // The notice sits beside the dropdown, above the fold; the estimator who typed the CPT code that
      // caused this is down at the procedure table. Only a run reaches here, so loading a draft / copy /
      // redo (which shows the notice from stored state) never toasts, and a Keep run carries no switch.
      toast?.current?.show({
        severity: "info",
        summary: "Service Type changed",
        detail: buildPracticeTypeSwitchMessage(
          eligibilityResponse.practiceTypeSwitch
        ),
        life: 6000,
      });
    }

    const isServiceDetailEmpty = (serviceDetail: any) => {
      return (
        serviceDetail == null ||
        (typeof serviceDetail === "object" &&
          Object.keys(serviceDetail).length === 0)
      );
    };
    const hospitalOutpatientIsEmpty = isServiceDetailEmpty(
      eligibilityResponse.details?.HospitalOutPatientSummary
    );
    const mriCtScanIsEmpty = isServiceDetailEmpty(
      eligibilityResponse.details?.MRI_CT_ScanSummary
    );
    const allFieldsAreNull =
      (hospitalOutpatientIsEmpty && mriCtScanIsEmpty) ||
      (eligibilityResponse.familyCalendarYearDeductible === null &&
        eligibilityResponse.individualCalendarYearDeductible === null &&
        eligibilityResponse.remainingDeductible === null &&
        eligibilityResponse.coinsurancePercent === null);

    // Handle recommended payer based on eligibility. The API has already read the benefits below
    // under the recommended payer (its network rule, Medicaid / Medicare handling, copay overrides;
    // see `benefitsDerivedUnderPayer`), so switching the payer field is all that is left to do here —
    // the benefit fields are filled from this same response, never re-derived on this side.
    if (eligibilityResponse.recommendedPayerBasedOnEligibility) {
      const recommendedPayer: IRecommendedPayerBasedOnEligibility =
        eligibilityResponse.recommendedPayerBasedOnEligibility;

      // Find the recommended payer in the available payers list
      const recommendedPayerOption = payersNameInfo.find(
        (payer: any) => payer.name === recommendedPayer.name
      );

      if (recommendedPayerOption) {
        // Show a message to the user about the automatic payer update
        errorMessageSetter(
          buildPayerSwitchMessage(
            recommendedPayer,
            eligibilityData.request.payerName,
            eligibilityResponse.planCoverageSummary?.PlanName
          )
        );
        // CLE-2845 — the stored request keeps the payer the check was SENT under. The API's
        // `wasPayerChangedSinceEligibility` recognises this switch from the recommendation on the
        // stored response, so the monthly re-run does not read it as a hand edit.
        setValue(
          isSecondaryInsurance ? "payerName2" : "payerName",
          recommendedPayer.name
        );
        setValue(isSecondaryInsurance ? "payer2" : "payer", {
          name: recommendedPayer.name,
          payerId: recommendedPayer.id,
        });
      }
    }

    // Always set benefitDetails BEFORE any early returns so we can display
    // basic plan coverage info even when detailed benefits aren't available
    setValue(
      isSecondaryInsurance ? "benefitDetails2" : "benefitDetails",
      eligibilityData
    );
    // Store the pverifyResponseId if available
    if (eligibilityResponse.pverifyResponseId) {
      setValue(
        isSecondaryInsurance
          ? "secondaryPverifyResponseId"
          : "pverifyResponseId",
        eligibilityResponse.pverifyResponseId
      );
    }
    if (isSecondaryInsurance) {
      setValue("secondaryBenefitsUpdatedAt", new Date().toISOString());
    } else {
      setValue("benefitsUpdatedAt", new Date().toISOString());
    }
    // CLE-2971 — tells the page trigger a check ran in THIS page session, under which code, and whether
    // it was a keep run (after which the trigger never re-runs on its own).
    keptPracticeTypeRef.current =
      sentKeep === true ? eligibilityData.request.practiceTypeCode : null;
    announcePracticeTypeRunCompleted({
      isSecondaryInsurance,
      practiceTypeCode: eligibilityData.request.practiceTypeCode,
      keepPracticeType: sentKeep === true,
      procedureCodes: (mapped.request.procedures ?? []).map(
        (procedure: { code: string }) => procedure.code
      ),
    });

    // CLE-2882 — the account is out of network for this plan, and nothing in-network is read. A field
    // with no out-of-network figure behind it — null, or a 0 the API flagged empty — is cleared rather
    // than left holding a $0 or an earlier check's number, so staff confirm it with the payer. Done
    // before the early returns below so it holds on every path.
    const isOutOfNetwork =
      eligibilityResponse.networkStatusOverride?.status === "OutOfNetwork";
    const blankOutOfNetwork = {
      deductible:
        isOutOfNetwork &&
        (eligibilityResponse.remainingDeductible === null ||
          !!eligibilityResponse.isRemainingDeductibleEmpty),
      outOfPocketMax:
        isOutOfNetwork &&
        (eligibilityResponse.remainingOutOfPocketMax === null ||
          !!eligibilityResponse.isRemainingOutOfPocketMaxEmpty),
      flatCopay: isOutOfNetwork && eligibilityResponse.flatCopay === null,
      coinsurancePercent:
        isOutOfNetwork &&
        (eligibilityResponse.coinsurancePercent === null ||
          !!eligibilityResponse.isCoinsurancePercentEmpty),
    };
    Object.entries(blankOutOfNetwork).forEach(([field, blank]) => {
      if (blank) setValue(isSecondaryInsurance ? `${field}2` : field, null);
    });

    // CLE-2675 — the payer answered, but every benefit it disclosed is out-of-network. The
    // normalized values are zeros of ignorance, not a real $0 plan: auto-filling them prices
    // the estimate off benefits the payer never disclosed. The response stays stored above
    // (same as the empty-benefits case); only the form fields are withheld. Manual entry
    // after calling the payer remains open.
    if (eligibilityResponse.hasOnlyOutOfNetworkBenefits) {
      errorMessageSetter(
        buildOutOfNetworkOnlyBenefitsMessage(eligibilityResponse.additionalInfo)
      );
      return;
    }

    if (eligibilityResponse.isEligibilityEmpty || allFieldsAreNull) {
      errorMessageSetter(eligibilityResponse.additionalInfo);
      return;
    }

    if (
      eligibilityResponse.coinsurancePercent !== null &&
      !blankOutOfNetwork.coinsurancePercent
    )
      setValue(
        isSecondaryInsurance ? "coinsurancePercent2" : "coinsurancePercent",
        Number(eligibilityResponse.coinsurancePercent)
      );
    if (eligibilityResponse.flatCopay !== null && !blankOutOfNetwork.flatCopay)
      setValue(
        isSecondaryInsurance ? "flatCopay2" : "flatCopay",
        Number(eligibilityResponse.flatCopay)
      );

    // Handle professional copay automation
    if (
      eligibilityResponse.professionalCopay !== null &&
      eligibilityResponse.professionalCopay > 0
    ) {
      setValue(
        isSecondaryInsurance
          ? "professionalFlatCopay2"
          : "professionalFlatCopay",
        Number(eligibilityResponse.professionalCopay)
      );
      // Auto-trigger professional copay display
      const profCopayTrigger = new CustomEvent("triggerProfessionalCopay", {
        detail: { value: eligibilityResponse.professionalCopay },
      });
      window.dispatchEvent(profCopayTrigger);
      // CLE-2866 — on an FEP BASIC plan the physician copay replaces the
      // physician-side deductible and co-insurance. Set here rather than in the
      // event listeners: both sides' listeners hear every event, and only this
      // button knows which side ran.
      if (eligibilityResponse.professionalCopayOnly) {
        setValue(
          isSecondaryInsurance
            ? "professionalDeductible2"
            : "professionalDeductible",
          false
        );
        setValue(
          isSecondaryInsurance
            ? "professionalCoinsurance2"
            : "professionalCoinsurance",
          false
        );
      }
    }
    if (
      eligibilityResponse.remainingDeductible !== null &&
      !blankOutOfNetwork.deductible
    )
      setValue(
        isSecondaryInsurance ? "deductible2" : "deductible",
        Number(eligibilityResponse.remainingDeductible)
      );
    if (
      eligibilityResponse.remainingOutOfPocketMax !== null &&
      !blankOutOfNetwork.outOfPocketMax
    )
      setValue(
        isSecondaryInsurance ? "outOfPocketMax2" : "outOfPocketMax",
        Number(eligibilityResponse.remainingOutOfPocketMax)
      );
    if (eligibilityResponse.isRemainingOutOfPocketMaxInfinity !== null)
      setValue(
        isSecondaryInsurance
          ? "isRemainingOutOfPocketMaxInfinity2"
          : "isRemainingOutOfPocketMaxInfinity",
        eligibilityResponse.isRemainingOutOfPocketMaxInfinity
      );
  };

  // CLE-2971 — one run at a time; a Service Type request held while it ran goes next (see `pendingRunRef`).
  const runPracticeTypeRequestRef = useRef<
    (detail: IPracticeTypeRunEventDetail) => void
  >(() => undefined);
  // CLE-2971 — each run holds the page's pending-eligibility counter, so the estimate cannot be created
  // on benefits about to be replaced. A held run takes its own hold BEFORE the settling run releases, so
  // the count never dips to zero between them. Every hold still open is released on unmount.
  const activityReleasesRef = useRef(new Set<() => void>());
  useEffect(() => {
    const releases = activityReleasesRef.current;
    return () => {
      releases.forEach((release) => release());
      releases.clear();
    };
  }, []);
  const holdActivity = () => {
    const release = holdEligibilityActivity({ getValues, setValue });
    activityReleasesRef.current.add(release);
    return () => {
      activityReleasesRef.current.delete(release);
      release();
    };
  };

  const runEligibility = async (options: IRunEligibilityOptions = {}) => {
    inFlightRef.current = true;
    const releaseActivity = holdActivity();
    try {
      await runEligibilityOnce(options);
    } finally {
      inFlightRef.current = false;
      const pending = pendingRunRef.current;
      pendingRunRef.current = null;
      if (pending) runPracticeTypeRequestRef.current(pending);
      // Success or failure: the page trigger takes its "Re-running eligibility" toast down on this.
      else announcePracticeTypeRunSettled({ isSecondaryInsurance });
      releaseActivity();
    }
  };

  // CLE-2883 — the banner's "Exception is approved" button asks for this side's check to be re-run with the
  // exception flag. A ref, so the listener registered once always calls the latest `runEligibility`.
  const runEligibilityRef = useRef(runEligibility);
  runEligibilityRef.current = runEligibility;
  useEffect(() => {
    const onRequest = (event: Event) => {
      const { detail } =
        event as CustomEvent<IOutOfNetworkExceptionEventDetail>;
      if (Boolean(detail?.isSecondaryInsurance) !== isSecondaryInsurance)
        return;
      runEligibilityRef.current({ outOfNetworkExceptionApproved: true });
    };
    window.addEventListener(OUT_OF_NETWORK_EXCEPTION_EVENT, onRequest);
    return () =>
      window.removeEventListener(OUT_OF_NETWORK_EXCEPTION_EVENT, onRequest);
  }, [isSecondaryInsurance]);

  // CLE-2971 — the Service Type notice's "Keep …" button and the page trigger ask this side to re-run.
  // `mapValuesToEligibility` reads `getValues()` at call time, so a code set here is what the request
  // sends. An approved out-of-network exception on the stored check is carried through: this is the
  // same check under another Service Type, not the plain button, which is how an exception is undone.
  // A request arriving mid-run is held (latest wins) and run when the current run settles, reading the form
  // as it is then.
  runPracticeTypeRequestRef.current = (detail: IPracticeTypeRunEventDetail) => {
    if (detail.practiceTypeCode) {
      setValue(
        isSecondaryInsurance ? "practiceType2" : "practiceType",
        detail.practiceTypeCode
      );
    }
    const values = getValues();
    const stored = isSecondaryInsurance
      ? values.benefitDetails2
      : values.benefitDetails;
    runEligibilityRef.current({
      keepPracticeType: detail.keepPracticeType === true,
      outOfNetworkExceptionApproved:
        stored?.request?.outOfNetworkExceptionApproved === true,
    });
  };
  useEffect(() => {
    const onRequest = (event: Event) => {
      const { detail } = event as CustomEvent<IPracticeTypeRunEventDetail>;
      if (
        !detail ||
        Boolean(detail.isSecondaryInsurance) !== isSecondaryInsurance
      )
        return;
      // Taken (run now or held): tells the page trigger a run will settle (`requestPracticeTypeRun`).
      event.preventDefault();
      if (inFlightRef.current) {
        pendingRunRef.current = detail;
        return;
      }
      runPracticeTypeRequestRef.current(detail);
    };
    window.addEventListener(PRACTICE_TYPE_RUN_EVENT, onRequest);
    return () => window.removeEventListener(PRACTICE_TYPE_RUN_EVENT, onRequest);
  }, [isSecondaryInsurance]);

  return (
    <div
      data-testid="footer_PayerCardFooter"
      className="flex justify-content-end"
    >
      <Button
        label="Run Eligibility"
        // Wrapped, so the click event is never read as options: a plain run sends no exception flag.
        onClick={() => runEligibility()}
        loading={runningEligibility}
        disabled={disabled}
        className="h-fit"
        icon="pi pi-sync"
      />
    </div>
  );
}
